import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  Unsubscribe,
  arrayUnion,
  arrayRemove,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase-config";
import { getCurrentUser } from "../auth";

export interface SavedPrompt {
  id: string;
  name: string;
  prompt: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ApiKeys {
  anthropic: string | null;
  openai: string | null;
  gemini: string | null;
}

export interface UserData {
  email: string;
  createdAt: Timestamp;
  lastActive: Timestamp;
  isAdmin: boolean;
  apiKeys: ApiKeys;
  savedPrompts: SavedPrompt[];
  enterpriseId: string | null;
  licensed: boolean;
}

export async function getUserData(): Promise<UserData | null> {
  const user = getCurrentUser();
  if (!user) return null;

  const snap = await getDoc(doc(db, "users", user.uid));
  if (!snap.exists()) return null;
  return snap.data() as UserData;
}

export function onUserDataChange(
  callback: (data: UserData | null) => void
): Unsubscribe {
  const user = getCurrentUser();
  if (!user) {
    callback(null);
    return () => {};
  }

  return onSnapshot(doc(db, "users", user.uid), (snap) => {
    callback(snap.exists() ? (snap.data() as UserData) : null);
  });
}

export async function updateApiKeys(keys: ApiKeys): Promise<void> {
  const user = getCurrentUser();
  if (!user) throw new Error("Not authenticated");

  await setDoc(doc(db, "users", user.uid), { apiKeys: keys }, { merge: true });
}

export async function addSavedPrompt(
  name: string,
  prompt: string
): Promise<void> {
  const user = getCurrentUser();
  if (!user) throw new Error("Not authenticated");

  const newPrompt: SavedPrompt = {
    id: generateId(),
    name,
    prompt,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  await updateDoc(doc(db, "users", user.uid), {
    savedPrompts: arrayUnion(newPrompt),
  });
}

export async function removeSavedPrompt(prompt: SavedPrompt): Promise<void> {
  const user = getCurrentUser();
  if (!user) throw new Error("Not authenticated");

  await updateDoc(doc(db, "users", user.uid), {
    savedPrompts: arrayRemove(prompt),
  });
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
}
